import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../components/contextProviders/AlertProvider";

export function useUtils() {
	const navigate = useNavigate();
	const { showAlert } = useAlert();
	const onlyAlnum = (v: string) => (v || "").replace(/[^0-9a-zA-Z]/g, "");

	const logout = () => {
		localStorage.removeItem("access-token");
		localStorage.removeItem("userType");
		localStorage.removeItem("username");
		localStorage.removeItem("region");
		navigate("/login");
	};

	const verifyPermission = (permissions: string[], module: string) => {
		if (!permissions) return true;
		const token = localStorage.getItem("access-token");
		if (!token) {
			logout();
			return true;
		}
		try {
			const invalid = !permissions.includes(module);
			if (invalid) {
				if (permissions.includes('INICIO')) {
					navigate(`/inicio`);
				} else if (permissions.includes('FORMS')) {
					navigate(`/formularios`);
				} else if (permissions.includes('EMPLOYEES')) {
					navigate(`/cadastro-funcionarios`);
				}
			}
			return invalid;
		} catch (error) {
			console.log(error);
			return true;
		}
	};

	const formatDate = (date: Date) => {
		const day = (date.getUTCDate()).toString().padStart(2, "0");
		const month = (date.getMonth() + 1).toString().padStart(2, "0");
		const year = date.getFullYear();
		return `${day}/${month}/${year}`;
	};

	const handleCopy = async (value: string) => {
		try {
			await navigator.clipboard.writeText(value);
			showAlert('Copiado');
		} catch {}
	};

	function formatStringDate(v: string) {
		if (!v) return "-";
		const datePart = v.split('T')[0];
		return datePart.split('-').reverse().join('/');
	}

	function formatStringFullDate(v: string | number) {
		if (!v) return "-";
		return dayjs(v).format("DD/MM/YYYY HH:mm");
	}

	function formatIsoDate(v: string) {
		return /^\d{4}-\d{2}-\d{2}$/.test(v || '') ? v.split('-').reverse().join('/') : v;
	}

	function formatCbo(v: string) {
		const digits = (v || '').replace(/\D/g, '');
		return digits.length === 6 ? `${digits.slice(0, 4)}-${digits.slice(4)}` : v;
	}

	function formatStringDateReverse(v: string) {
		if (!v) return "-";
		return v.split('-').reverse().join('/');
	}

	function formatDocument(raw: string) {
		const v = onlyAlnum(raw);
		if (!v) return "";
		if (v.length <= 11) {
			const parts = [
			v.slice(0, 3),
			v.slice(3, 6),
			v.slice(6, 9),
			v.slice(9, 11),
			].filter(Boolean);
			if (parts.length === 0) return "";
			if (parts.length === 1) return parts[0];
			if (parts.length === 2) return `${parts[0]}.${parts[1]}`;
			if (parts.length === 3) return `${parts[0]}.${parts[1]}.${parts[2]}`;
			return `${parts[0]}.${parts[1]}.${parts[2]}-${parts[3]}`;
		}
		const g1 = v.slice(0, 2);
		const g2 = v.slice(2, 5);
		const g3 = v.slice(5, 8);
		const g4 = v.slice(8, 12);
		const g5 = v.slice(12, 14);
		const groups = [g1, g2, g3, g4, g5].filter(Boolean);
		let out = "";
		if (groups[0]) out += groups[0];
		if (groups[1]) out += `.${groups[1]}`;
		if (groups[2]) out += `.${groups[2]}`;
		if (groups[3]) out += `/${groups[3]}`;
		if (groups[4]) out += `-${groups[4]}`;
		return out;
	};

	function validateCPF(strCPF: string): boolean {
		let soma: number;
		let resto: number;
		strCPF = strCPF.replace(/\D/g, '');
		soma = 0;
		if (strCPF === '00000000000') {
			return false;
		}
		if (0 === parseInt(strCPF, 10) % 11111111111) {
			return false;
		}
		for (let i = 1; i <= 9; i++) {
			soma = soma + parseInt(strCPF.substring(i - 1, i), 10) * (11 - i);
		}
		resto = (soma * 10) % 11;
		if ((resto === 10) || (resto === 11)) {
			resto = 0;
		}
		if (resto !== parseInt(strCPF.substring(9, 10), 10)) {
			return false;
		}
		soma = 0;
		for (let i = 1; i <= 10; i++) {
			soma = soma + parseInt(strCPF.substring(i - 1, i), 10) * (12 - i);
		}
		resto = (soma * 10) % 11;
		if ((resto === 10) || (resto === 11)) {
			resto = 0;
		}
		if (resto !== parseInt(strCPF.substring(10, 11), 10)) {
			return false;
		}
		return true;
	}

	function validateCNPJ(cnpj : string) {
		if (!cnpj) return false;
		const regexCnpj = /^\d{2}\.[A-Za-z0-9]{3}\.[A-Za-z0-9]{3}\/[A-Za-z0-9]{4}\-[0-9]{2}$/;
		return cnpj.length === 18 && regexCnpj.test(cnpj);
	}

	function parseDate(dateStr: string): Date {
		if (!dateStr) return new Date();
		const [day, month, yearAndHour] = dateStr.split("/");
		const [year, hourMinute] = yearAndHour.split(" ");
		const [hour, minute] = hourMinute.split(":");
		return new Date(
			Number(year),
			Number(month) - 1,
			Number(day),
			Number(hour),
			Number(minute)
		);
	};

	return { logout, verifyPermission, formatDate, formatIsoDate, formatCbo,
		handleCopy, formatStringDate, formatStringDateReverse, formatDocument,
		validateCPF, validateCNPJ, formatStringFullDate, parseDate };
}